import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, Image as ImageIcon, Video, Package, Eye, EyeOff, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";

export function DeliveryDialog({ campaignId, campaignTitle, role, triggerClassName, triggerTestId }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/campaigns/${campaignId}/deliverables`).then(({ data }) => setItems(data.deliverables)).catch(() => {});
  }, [campaignId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/campaigns/${campaignId}/deliverables`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("uploaded"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/deliverables/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur de téléchargement");
    }
  };

  const togglePublish = async (d) => {
    try {
      await api.post(`/deliverables/${d.id}/publish`, { published: !d.published });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={triggerTestId} className={triggerClassName}>
          <Package size={13} /> {t("deliverables")}
        </button>
      </DialogTrigger>
      <DialogContent className="border-cardline bg-card2 text-white">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("deliverySpace")}</DialogTitle>
          <DialogDescription className="text-slate-400">{campaignTitle} — {t("deliverInfo")}</DialogDescription>
        </DialogHeader>

        {role === "creator" && (
          <div>
            <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onUpload} className="hidden" data-testid="delivery-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="delivery-upload-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gold/50 bg-gold/5 py-3 text-sm font-semibold text-gold disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {busy ? t("uploading") : t("addMedia")}
            </button>
          </div>
        )}

        <div className="max-h-72 space-y-2 overflow-y-auto" data-testid="delivery-list">
          {items.length === 0 && <p className="py-8 text-center text-sm text-slate-500">{t("noDeliverables")}</p>}
          {items.map((d) => (
            <div key={d.id} data-testid={`deliverable-${d.id}`} className="flex items-center gap-3 rounded-xl bg-ink2/50 p-2.5">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gold/10 text-gold">
                {d.kind === "video" ? <Video size={16} /> : <ImageIcon size={16} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{d.original_filename}</div>
                <div className="text-[11px] text-slate-400">{(d.size / 1024 / 1024).toFixed(1)} Mo · {d.creator_name}</div>
              </div>
              <button onClick={() => download(d)} data-testid={`download-${d.id}`} className="grid h-8 w-8 place-items-center rounded-lg bg-ink2 text-slate-200 hover:text-gold" title={t("download")}>
                <Download size={15} />
              </button>
              {role === "commerce" && (
                <button
                  onClick={() => togglePublish(d)}
                  data-testid={`publish-${d.id}`}
                  className={`grid h-8 w-8 place-items-center rounded-lg ${d.published ? "bg-emerald-500/20 text-emerald-400" : "bg-ink2 text-slate-400"}`}
                  title={d.published ? t("published") : t("publish")}
                >
                  {d.published ? <Eye size={15} /> : <EyeOff size={15} />}
                </button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
